/**
 * Sync Reports parse boundary (WSRADM-0014).
 *
 * A bad *row* is dropped; a malformed *envelope* (jobs/journal missing a
 * `content` array, or last-runs missing a `lastRuns` array) throws `ApiError`
 * `UNKNOWN`. `{ jobs: [...] }` is never treated as success.
 */
import { ApiError } from '@/api/errors';
import { asNumber, asText, isObject } from '@/api/parse';
import type { PagedResponse } from '@/api/types';
import type {
  IngestionJobView,
  LastRunsResponse,
  LastRunView,
  SyncDecisionView,
} from './types';

function envelopeError(): never {
  throw new ApiError('UNKNOWN', 0, {});
}

function parseNullableText(value: unknown): string | null {
  return asText(value);
}

function parseRunCounters(value: unknown): Record<string, number> | null | undefined {
  if (value == null) return null;
  if (!isObject(value) || Array.isArray(value)) return undefined;
  const out: Record<string, number> = {};
  for (const key of Object.keys(value)) {
    const n = asNumber(value[key]);
    if (n === null) return undefined;
    out[key] = n;
  }
  return out;
}

function parsePagedMeta(
  body: Record<string, unknown>,
  contentLength: number,
): Pick<PagedResponse<unknown>, 'page' | 'size' | 'totalElements' | 'totalPages'> {
  return {
    page: asNumber(body['page']) ?? 0,
    size: asNumber(body['size']) ?? 20,
    totalElements: asNumber(body['totalElements']) ?? contentLength,
    totalPages: asNumber(body['totalPages']) ?? 1,
  };
}

export function parseIngestionJob(raw: unknown): IngestionJobView | null {
  if (!isObject(raw) || Array.isArray(raw)) return null;
  const id = asNumber(raw['id']);
  const jobType = asText(raw['jobType']);
  const status = asText(raw['status']);
  const scheduledFor = asText(raw['scheduledFor']);
  const attempt = asNumber(raw['attempt']);
  const maxAttempts = asNumber(raw['maxAttempts']);
  const rescued = asNumber(raw['rescued']);
  const maxRescues = asNumber(raw['maxRescues']);
  const runCounters = parseRunCounters(raw['runCounters']);
  if (
    id === null ||
    jobType === null ||
    status === null ||
    scheduledFor === null ||
    attempt === null ||
    maxAttempts === null ||
    rescued === null ||
    maxRescues === null ||
    runCounters === undefined
  ) {
    return null;
  }
  return {
    id,
    jobType,
    status,
    scheduledFor,
    claimedAt: parseNullableText(raw['claimedAt']),
    claimedBy: parseNullableText(raw['claimedBy']),
    startedAt: parseNullableText(raw['startedAt']),
    finishedAt: parseNullableText(raw['finishedAt']),
    leaseExpiresAt: parseNullableText(raw['leaseExpiresAt']),
    heartbeatAt: parseNullableText(raw['heartbeatAt']),
    executionDeadline: parseNullableText(raw['executionDeadline']),
    attempt,
    maxAttempts,
    nextRetryAt: parseNullableText(raw['nextRetryAt']),
    rescued,
    maxRescues,
    watermarkFrom: parseNullableText(raw['watermarkFrom']),
    watermarkTo: parseNullableText(raw['watermarkTo']),
    errorCode: parseNullableText(raw['errorCode']),
    errorDetail: parseNullableText(raw['errorDetail']),
    createdAt: parseNullableText(raw['createdAt']),
    updatedAt: parseNullableText(raw['updatedAt']),
    runKind: parseNullableText(raw['runKind']),
    runCounters,
  };
}

export function parseSyncDecision(raw: unknown): SyncDecisionView | null {
  if (!isObject(raw) || Array.isArray(raw)) return null;
  const id = asNumber(raw['id']);
  const jobId = asNumber(raw['jobId']);
  const severity = asText(raw['severity']);
  const action = asText(raw['action']);
  const entityKind = asText(raw['entityKind']);
  const entityKey = asText(raw['entityKey']);
  const summary = asText(raw['summary']);
  const occurredAt = asText(raw['occurredAt']);
  const factsRaw = raw['facts'];
  if (
    id === null ||
    jobId === null ||
    severity === null ||
    action === null ||
    entityKind === null ||
    entityKey === null ||
    summary === null ||
    occurredAt === null
  ) {
    return null;
  }
  if (factsRaw != null && (!isObject(factsRaw) || Array.isArray(factsRaw))) {
    return null;
  }
  return {
    id,
    jobId,
    jobType: asText(raw['jobType']) ?? '',
    severity,
    action,
    entityKind,
    entityKey,
    summary,
    facts: factsRaw == null ? null : factsRaw,
    occurredAt,
  };
}

export function parseLastRun(raw: unknown): LastRunView | null {
  if (!isObject(raw) || Array.isArray(raw)) return null;
  const id = asNumber(raw['id']);
  const jobType = asText(raw['jobType']);
  const finishedAt = asText(raw['finishedAt']);
  const status = asText(raw['status']);
  const counters = parseRunCounters(raw['counters']);
  if (id === null || jobType === null || finishedAt === null || status === null || counters === undefined) {
    return null;
  }
  const caughtUpRaw = raw['caughtUp'];
  return {
    jobType,
    id,
    finishedAt,
    runKind: parseNullableText(raw['runKind']),
    rows: asNumber(raw['rows']),
    counters,
    caughtUp: typeof caughtUpRaw === 'boolean' ? caughtUpRaw : null,
    errorDetail: parseNullableText(raw['errorDetail']),
    status,
  };
}

export function parseJobsPage(body: unknown): PagedResponse<IngestionJobView> {
  if (!isObject(body) || !Array.isArray(body['content'])) envelopeError();
  const content: IngestionJobView[] = [];
  for (const row of body['content']) {
    const parsed = parseIngestionJob(row);
    if (parsed !== null) content.push(parsed);
  }
  return { content, ...parsePagedMeta(body, content.length) };
}

export function parseDecisionsPage(body: unknown): PagedResponse<SyncDecisionView> {
  if (!isObject(body) || !Array.isArray(body['content'])) envelopeError();
  const content: SyncDecisionView[] = [];
  for (const row of body['content']) {
    const parsed = parseSyncDecision(row);
    if (parsed !== null) content.push(parsed);
  }
  return { content, ...parsePagedMeta(body, content.length) };
}

export function parseLastRuns(body: unknown): LastRunsResponse {
  if (!isObject(body) || !Array.isArray(body['lastRuns'])) envelopeError();
  const lastRuns: LastRunView[] = [];
  for (const row of body['lastRuns']) {
    const parsed = parseLastRun(row);
    if (parsed !== null) lastRuns.push(parsed);
  }
  return { lastRuns };
}
